import type { AppContext } from "../Page.js";
import { h } from "../dom.js";
import { Dialog } from "./Dialog.js";

const EXAMPLE = `type,name,damage,label,min,target,batch,priority,enabled,critical,cpus
item,minecraft:iron_ingot,0,Iron Ingot,1000,2000,256,0,true,false,
fluid,water,,Water,100000,200000,64000,0,true,false,
essentia,ignis,,Ignis,100,200,64,5,true,true,`;

/** Bulk import of rules from CSV or JSON (the same formats the export produces). */
export function importRules(ctx: AppContext): Promise<boolean> {
  return new Promise((resolve) => {
    const dialog = new Dialog("Import rules", true);
    const format = h("select", null, h("option", { value: "csv" }, "CSV"), h("option", { value: "json" }, "JSON"));
    const text = h("textarea", { rows: 12, placeholder: EXAMPLE, spellcheck: false });
    const file = h("input", { type: "file", accept: ".csv,.json,text/csv,application/json" });
    const overwrite = h("input", { type: "checkbox" });
    const result = h("div", { class: "small" });
    let imported = false;
    dialog.el.addEventListener("close", () => resolve(imported));
    file.addEventListener("change", async () => {
      const f = file.files?.[0];
      if (!f) return;
      text.value = await f.text();
      format.value = f.name.endsWith(".json") ? "json" : "csv";
    });
    const run = h("button", {
      type: "button",
      class: "primary",
      onclick: async () => {
        run.disabled = true;
        try {
          const r = await ctx.api.importRules(format.value as "csv" | "json", text.value, overwrite.checked);
          imported = imported || r.created + r.updated > 0;
          result.replaceChildren(h("p", null, `${r.created} added, ${r.updated} updated, ${r.skipped} skipped, ${r.errors.length} errors.`));
          if (r.errors.length) {
            result.appendChild(h("ul", { class: "error" }, ...r.errors.slice(0, 20).map((e) => h("li", null, `Row ${e.index + 1}: ${e.error}`))));
          }
        } catch (err) {
          result.replaceChildren(h("p", { class: "error" }, (err as Error).message));
        } finally {
          run.disabled = false;
        }
      },
    }, "Import");
    dialog.body.append(
      h("p", { class: "muted small" }, "Paste rules or choose a file. CSV needs a header row; the columns are those of the export. Rows for stacks that already have a rule are skipped unless you tick overwrite."),
      h("div", { class: "row" }, h("label", null, "Format", format), h("label", { class: "grow" }, "File", file)),
      text,
      h("label", { class: "check" }, overwrite, "Overwrite existing rules"),
      result,
    );
    dialog.footer.append(h("button", { type: "button", onclick: () => dialog.close() }, "Close"), run);
    dialog.open();
  });
}
